import React, { FC } from 'react';
import { RiArrowLeftSLine, RiArrowRightSLine } from 'react-icons/ri';
import { useTranslation } from 'react-i18next';

interface PaginationProps {
  allRows: number;
  rowsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
}

const Pagination: FC<PaginationProps> = ({
  allRows,
  rowsPerPage,
  currentPage,
  onPageChange,
}) => {
  const { t, i18n } = useTranslation('common');
  const totalPages = Math.ceil(allRows / rowsPerPage);

  const changePage = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      onPageChange(newPage);
    }
  };

  const renderPageNumbers = (): JSX.Element[] => {
    let pages: JSX.Element[] = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <button
            key={i}
            className={`size-8 border rounded-md text-caption ${
              currentPage === i ? 'bg-primary/300 text-white' : ''
            }`}
            onClick={() => changePage(i)}
          >
            {i}
          </button>
        );
      }
    } else {
      pages.push(
        <button
          key={1}
          className={`size-8 border rounded-md ${
            currentPage === 1 ? 'bg-primary/300 text-white' : ''
          }`}
          onClick={() => changePage(1)}
        >
          1
        </button>,
        <button
          key={2}
          className={`size-8 border rounded-md ${
            currentPage === 2 ? 'bg-primary/300 text-white' : ''
          }`}
          onClick={() => changePage(2)}
        >
          2
        </button>
      );

      if (currentPage > 3 && currentPage < totalPages - 2) {
        pages.push(
          <span key="ellipsis-start" className="py-1 px-2">
            ...
          </span>
        );
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(
            <button
              key={i}
              className={`size-8 border rounded-md ${
                currentPage === i ? 'bg-primary/300 text-white' : ''
              }`}
              onClick={() => changePage(i)}
            >
              {i}
            </button>
          );
        }
        pages.push(
          <span key="ellipsis-end" className="size-8">
            ...
          </span>
        );
      } else if (currentPage <= 3) {
        for (let i = 3; i <= 4; i++) {
          pages.push(
            <button
              key={i}
              className={`size-8 border rounded-md ${
                currentPage === i ? 'bg-primary/300 text-white' : ''
              }`}
              onClick={() => changePage(i)}
            >
              {i}
            </button>
          );
        }
        pages.push(
          <span key="ellipsis-end" className="size-8">
            ...
          </span>
        );
      } else {
        pages.push(
          <span key="ellipsis-start" className="size-8">
            ...
          </span>
        );
        for (let i = totalPages - 3; i <= totalPages - 2; i++) {
          pages.push(
            <button
              key={i}
              className={`size-8 border rounded-md ${
                currentPage === i ? 'bg-primary/300 text-white' : ''
              }`}
              onClick={() => changePage(i)}
            >
              {i}
            </button>
          );
        }
      }
      pages.push(
        <button
          key={totalPages - 1}
          className={`size-8 border rounded-md ${
            currentPage === totalPages - 1 ? 'bg-primary/300 text-white' : ''
          }`}
          onClick={() => changePage(totalPages - 1)}
        >
          {totalPages - 1}
        </button>,
        <button
          key={totalPages}
          className={`size-8 border rounded-md ${
            currentPage === totalPages ? 'bg-primary/300 text-white' : ''
          }`}
          onClick={() => changePage(totalPages)}
        >
          {totalPages}
        </button>
      );
    }
    return pages;
  };

  return (
    <div className="flex flex-row justify-between items-center">
      {i18n.language === 'jp' ? (
        <div className="text-neutral/100">
          {allRows} 件中{' '}
          {Math.min(rowsPerPage * (currentPage - 1) + 1, allRows)}-
          {Math.min(rowsPerPage * currentPage, allRows)}件目を表示中
        </div>
      ) : (
        <div className="text-neutral/100">
          Showing data {Math.min(rowsPerPage * (currentPage - 1) + 1, allRows)}{' '}
          to {Math.min(rowsPerPage * currentPage, allRows)} of {allRows} entries
        </div>
      )}
      <div className="space-x-1 flex items-center">
        <button
          className={`px-3 h-8 border rounded-md flex items-center justify-center ${
            currentPage === 1 || totalPages === 0 ? 'text-neutral/75' : ''
          }`}
          onClick={() => changePage(currentPage - 1)}
          disabled={currentPage === 1 || totalPages === 0}
        >
          <RiArrowLeftSLine className="mr-1" />
          {t('pagination.back')}
        </button>
        {renderPageNumbers()}
        <button
          className={`px-3 h-8 border rounded-md flex items-center justify-center ${
            currentPage === totalPages || totalPages === 0
              ? 'text-gray-400'
              : ''
          }`}
          onClick={() => changePage(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
        >
          {t('pagination.next')}
          <RiArrowRightSLine className="ml-1" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
